import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Camera, Loader2 } from "lucide-react";
import { useApp, API, waLink, WA_MSG_EN, WA_MSG_AR } from "@/context/AppContext";
import { BRANDS, CITIES, CATEGORY_KEYS, CATEGORY_LABELS } from "@/i18n";

const field = "w-full bg-[#0a0a0a] border border-[#27272a] text-white px-4 py-3 text-sm outline-none focus:border-[#EAB308] transition-colors placeholder:text-zinc-600";

const QuotationForm = ({ compact = false, prefill = {} }) => {
  const { tr, lang } = useApp();
  const f = tr.form;
  const [data, setData] = useState({
    customer_name: "", company_name: "", mobile: "", whatsapp: "", email: "", city: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "", tyre_size: "", preferred_brand: "",
    tyre_category: "", quantity: "", delivery_location: "", required_date: "", notes: "",
    preferred_contact: "whatsapp", consent: false, ...prefill,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.customer_name || !data.mobile || !data.city) return toast.error(f.errRequired);
    if (!data.consent) return toast.error(f.errConsent);
    setLoading(true);
    try {
      await axios.post(`${API}/quotations`, data);
      setDone(true);
      toast.success(f.success);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        data-testid="quotation-success" className="bg-[#121212] border border-[#006C35] p-10 text-center">
        <CheckCircle2 className="h-16 w-16 text-[#006C35] mx-auto mb-4" />
        <p className="text-white text-lg leading-relaxed max-w-lg mx-auto">{f.success}</p>
        <a href={waLink(lang === "ar" ? WA_MSG_AR : WA_MSG_EN)} target="_blank" rel="noopener noreferrer"
           className="inline-block mt-6 text-[#25D366] font-semibold hover:underline">{tr.cta.whatsapp}</a>
      </motion.div>
    );
  }

  const req = <span className="text-[#EAB308]">*</span>;

  return (
    <form onSubmit={submit} data-testid="quotation-form" className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.name} {req}</label>
        <input className={field} value={data.customer_name} onChange={set("customer_name")} data-testid="qf-name" />
      </div>
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.company}</label>
        <input className={field} value={data.company_name} onChange={set("company_name")} data-testid="qf-company" />
      </div>
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.mobile} {req}</label>
        <input className={field} value={data.mobile} onChange={set("mobile")} placeholder="+966 5X XXX XXXX" data-testid="qf-mobile" dir="ltr" />
      </div>
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.whatsapp}</label>
        <input className={field} value={data.whatsapp} onChange={set("whatsapp")} placeholder="+966 5X XXX XXXX" data-testid="qf-whatsapp" dir="ltr" />
      </div>
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.email}</label>
        <input type="email" className={field} value={data.email} onChange={set("email")} data-testid="qf-email" dir="ltr" />
      </div>
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.city} {req}</label>
        <select className={field} value={data.city} onChange={set("city")} data-testid="qf-city">
          <option value="">{tr.search.any}</option>
          {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {!compact && <>
        <div><label className="text-xs text-zinc-400 mb-1.5 block">{f.make}</label><input className={field} value={data.vehicle_make} onChange={set("vehicle_make")} data-testid="qf-make" /></div>
        <div><label className="text-xs text-zinc-400 mb-1.5 block">{f.model}</label><input className={field} value={data.vehicle_model} onChange={set("vehicle_model")} data-testid="qf-model" /></div>
        <div><label className="text-xs text-zinc-400 mb-1.5 block">{f.year}</label><input className={field} value={data.vehicle_year} onChange={set("vehicle_year")} data-testid="qf-year" /></div>
      </>}

      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.size}</label>
        <input className={field} value={data.tyre_size} onChange={set("tyre_size")} placeholder="265/70 R16" data-testid="qf-size" dir="ltr" />
      </div>
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.brand}</label>
        <select className={field} value={data.preferred_brand} onChange={set("preferred_brand")} data-testid="qf-brand">
          <option value="">{tr.search.any}</option>
          {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.category}</label>
        <select className={field} value={data.tyre_category} onChange={set("tyre_category")} data-testid="qf-category">
          <option value="">{tr.search.any}</option>
          {CATEGORY_KEYS.map((k) => <option key={k} value={CATEGORY_LABELS.en[k]}>{CATEGORY_LABELS[lang][k]}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block">{f.qty}</label>
        <input type="number" min="1" className={field} value={data.quantity} onChange={set("quantity")} placeholder="4" data-testid="qf-qty" />
      </div>

      {!compact && <>
        <div><label className="text-xs text-zinc-400 mb-1.5 block">{f.delivery}</label><input className={field} value={data.delivery_location} onChange={set("delivery_location")} data-testid="qf-delivery" /></div>
        <div><label className="text-xs text-zinc-400 mb-1.5 block">{f.date}</label><input type="date" className={field} value={data.required_date} onChange={set("required_date")} data-testid="qf-date" /></div>
        <div>
          <label className="text-xs text-zinc-400 mb-1.5 block">{f.contact}</label>
          <select className={field} value={data.preferred_contact} onChange={set("preferred_contact")} data-testid="qf-contact">
            <option value="whatsapp">WhatsApp</option><option value="call">{tr.cta.call}</option><option value="email">Email</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-zinc-400 mb-1.5 block">{f.notes}</label>
          <textarea rows={3} className={field} value={data.notes} onChange={set("notes")} data-testid="qf-notes" />
        </div>
      </>}

      <div className="md:col-span-2 flex flex-wrap items-center gap-3">
        <a href={waLink(lang === "ar" ? WA_MSG_AR : WA_MSG_EN)} target="_blank" rel="noopener noreferrer"
           data-testid="qf-wa-photo" className="inline-flex items-center gap-2 text-sm text-[#25D366] border border-[#25D366]/40 hover:border-[#25D366] px-4 py-2.5 transition-colors">
          <Camera className="h-4 w-4" /> {f.waPhoto}
        </a>
      </div>

      <label className="md:col-span-2 flex items-start gap-3 cursor-pointer text-sm text-zinc-300">
        <input type="checkbox" checked={data.consent} onChange={set("consent")} data-testid="qf-consent"
               className="mt-1 h-4 w-4 accent-[#EAB308]" />
        <span>{f.consent} {req}</span>
      </label>

      <button type="submit" disabled={loading} data-testid="qf-submit"
        className="md:col-span-2 bg-[#EAB308] text-[#050505] font-bold py-4 uppercase tracking-wider hover:bg-[#ca8a04] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> {f.sending}</> : f.submit}
      </button>
    </form>
  );
};

export default QuotationForm;
